import * as React from "react";
import type { BriefField } from "@excellent/core";
import { Input } from "../../input";
import { Textarea } from "../../textarea";
import { PersonPicker } from "../person-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";

/**
 * The one editor for a single brief field, shared by BriefList (dense,
 * all-at-once) and BriefCarousel (one-at-a-time). Previously duplicated in
 * both; kept in one place so behavior — especially the select "Other → type
 * your own" escape — can't drift between the two surfaces.
 *
 * Values are `unknown` at the boundary so multi/number/array fields aren't
 * stringified prematurely.
 */
export function BriefFieldInput({
  field,
  value,
  onChange,
  onCommit,
  disabled,
  autofocus = false,
}: {
  field: BriefField;
  value: unknown;
  onChange: (v: unknown) => void;
  onCommit: () => void;
  disabled: boolean;
  /** Focus the input on mount (dense list uses this for the focused field). */
  autofocus?: boolean;
}) {
  switch (field.type) {
    case "longtext":
      return (
        <Textarea
          aria-label={field.label}
          value={typeof value === "string" ? value : ""}
          placeholder={field.placeholder}
          rows={3}
          disabled={disabled}
          autoFocus={autofocus}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
        />
      );

    case "select":
      return (
        <SelectField
          field={field}
          value={value}
          onChange={onChange}
          onCommit={onCommit}
          disabled={disabled}
        />
      );

    case "multi": {
      const raw = Array.isArray(value) ? (value as string[]).join(", ") : "";
      return (
        <Input
          aria-label={field.label}
          value={raw}
          placeholder={field.placeholder ?? "Comma-separated values"}
          disabled={disabled}
          autoFocus={autofocus}
          onChange={(e) => {
            const items = e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            onChange(items);
          }}
          onBlur={onCommit}
        />
      );
    }

    case "url":
      return (
        <Input
          type="url"
          aria-label={field.label}
          value={typeof value === "string" ? value : ""}
          placeholder={field.placeholder ?? "https://..."}
          disabled={disabled}
          autoFocus={autofocus}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
        />
      );

    case "number":
      return (
        <Input
          type="number"
          aria-label={field.label}
          value={
            typeof value === "string" || typeof value === "number"
              ? String(value ?? "")
              : ""
          }
          placeholder={field.placeholder}
          disabled={disabled}
          autoFocus={autofocus}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
        />
      );

    case "urn-person":
    case "urn-people":
      return (
        <PersonPicker
          value={value}
          multi={field.type === "urn-people"}
          onChange={onChange}
          onCommit={onCommit}
          disabled={disabled}
          autoFocus={autofocus}
          ariaLabel={field.label}
          placeholder={field.placeholder ?? "Select a team member…"}
        />
      );

    case "text":
    default:
      return (
        <Input
          type={field.sensitive ? "password" : "text"}
          aria-label={field.label}
          value={typeof value === "string" ? value : ""}
          placeholder={field.placeholder}
          disabled={disabled}
          autoFocus={autofocus}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
        />
      );
  }
}

/**
 * A select that accepts a value outside its preset list. The preset options
 * never cover every real-world answer (e.g. "Digital Ocean" as a hosting
 * provider), so choosing "Other" reveals a free-text input and the typed
 * value is stored verbatim. On reload, any stored value that isn't a known
 * option is treated as a custom entry — the select shows "Other" and the
 * text input is pre-filled — so custom answers survive round-trips.
 */
function SelectField({
  field,
  value,
  onChange,
  onCommit,
  disabled,
}: {
  field: BriefField;
  value: unknown;
  onChange: (v: unknown) => void;
  onCommit: () => void;
  disabled: boolean;
}) {
  const options = field.options ?? [];
  const optionValues = options.map((o) => o.value);
  const hasOtherOption = optionValues.includes("other");
  const stringValue = typeof value === "string" ? value : "";
  const isCustomValue = stringValue !== "" && !optionValues.includes(stringValue);

  // Sticky "Other" mode: picking Other clears the value (a bare "Other" isn't
  // a real answer), so we remember the choice until the user types or picks a
  // preset. A custom stored value also implies Other mode.
  const [otherMode, setOtherMode] = React.useState(isCustomValue);
  React.useEffect(() => {
    if (isCustomValue) setOtherMode(true);
  }, [isCustomValue]);

  const showCustom = otherMode || isCustomValue;
  const selectValue = showCustom ? "other" : stringValue;

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={selectValue}
        onValueChange={(v) => {
          if (v === "other") {
            setOtherMode(true);
            // Don't commit yet — wait for the free-text entry.
            if (!isCustomValue) onChange("");
          } else {
            setOtherMode(false);
            onChange(v);
            // Selects have no onBlur — commit immediately.
            setTimeout(onCommit, 0);
          }
        }}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={field.placeholder ?? "Select an option"} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
          {!hasOtherOption ? (
            <SelectItem value="other">Other…</SelectItem>
          ) : null}
        </SelectContent>
      </Select>
      {showCustom ? (
        <Input
          aria-label={`${field.label} (custom)`}
          value={isCustomValue ? stringValue : ""}
          placeholder="Type your answer…"
          disabled={disabled}
          autoFocus
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
        />
      ) : null}
    </div>
  );
}

/** Empty default for a field, by type — used when a draft has no value yet. */
export function defaultForType(field: BriefField): unknown {
  switch (field.type) {
    case "multi":
    case "urn-people":
      return [] as string[];
    case "number":
      return "";
    default:
      return "";
  }
}

/** Value equality that handles the array (multi) case. */
export function equalValues(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  return false;
}
